import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useListTenantsWithToken, type Tenant } from '../../api/platformTenants';

export interface TenantPickerProps {
  value: number | null;
  onChange: (tenantId: number) => void;
}

/**
 * Tenant picker (platform-admin). Logic unchanged; restyled with Bootstrap/
 * Velzon dropdown classes. Keeps listbox/option roles for accessibility + tests.
 */
export function TenantPicker({ value, onChange }: TenantPickerProps) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const { data, isLoading, error } = useListTenantsWithToken(token);
  const [open, setOpen] = useState(false);

  const tenants = data?.data ?? [];
  const selected = tenants.find((t) => t.id === value) ?? null;

  return (
    <div className="dropdown position-relative" style={{ minWidth: 200 }}>
      <button
        type="button"
        className="btn btn-soft-secondary btn-sm dropdown-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Tenant"
      >
        {isLoading ? 'Caricamento…' : selected ? selected.code : 'Tenant'}
      </button>
      <ul
        role="listbox"
        hidden={!open}
        className={'dropdown-menu' + (open ? ' show' : '')}
        style={{ display: open ? 'block' : 'none', maxHeight: 280, overflowY: 'auto', minWidth: 220 }}
      >
        {error && <li className="dropdown-item-text text-danger">Errore: {String(error)}</li>}
        {tenants.map((t: Tenant) => (
          <li key={t.id}>
            <button
              type="button"
              role="option"
              aria-selected={t.id === value}
              className={'dropdown-item' + (t.id === value ? ' active' : '')}
              onClick={() => { onChange(t.id); setOpen(false); }}
            >
              <span className="font-monospace">{t.code}</span>
              <span className="d-block text-muted small">{t.name}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
